import React, { useRef } from "react";
import { Form } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import WarrantyPage from "../common/WarrantyPage";
import { useWarrantyNav } from "../common/useWarrantyNav";
import ComponentIcons from "./ComponentIcons";
import ModelImages from "./ModelImages";
import { CATALOGUE_COUNTRIES, DEFAULT_CATALOGUE_COUNTRY, catalogueTabs } from "./catalogueFields";
import "../warranty.css";

const TABS = catalogueTabs();

// Warranty > Catalogue images: the component icons and the model image overrides the Amigo app
// shows on its Warranty screen. The tab and the market sit in the address, so a link keeps them.
const CatalogueImages = () => {
  const [params, setParams] = useSearchParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.PLANS_ADMIN);
  useWarrantyNav(allowed);
  const tabRefs = useRef({});

  const tab = TABS.some((item) => item.key === params.get("tab")) ? params.get("tab") : TABS[0].key;
  const country = CATALOGUE_COUNTRIES.some((item) => item.code === params.get("country"))
    ? params.get("country")
    : DEFAULT_CATALOGUE_COUNTRY;

  const choose = (next) => setParams({ tab: next, country }, { replace: true });

  const onKeyDown = (event, index) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) {
      return;
    }
    event.preventDefault();
    const next = TABS[(index + step + TABS.length) % TABS.length].key;
    choose(next);
    const node = tabRefs.current[next];
    if (node) {
      node.focus();
    }
  };

  return (
    <WarrantyPage
      title="Catalogue images"
      sub="Icons and images must be https URLs (for example on cdn.emotorad.com); the app shows them on the Warranty screen."
      allowed={allowed}
      deniedTitle="You do not have access to the catalogue images"
      deniedText="Ask an admin to give you the Warranty Assure Plans permission."
      actions={
        <Form.Group controlId="warrantyCatalogueCountry" className="warranty-catalogue-country">
          <Form.Label className="warranty-sr-only">Market</Form.Label>
          <Form.Select
            value={country}
            onChange={(event) => setParams({ tab, country: event.target.value }, { replace: true })}
          >
            {CATALOGUE_COUNTRIES.map((item) => (
              <option key={item.code} value={item.code}>
                {item.label} ({item.code})
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      }
    >
      <div className="warranty-tabs" role="tablist" aria-label="Catalogue images">
        {TABS.map((item, index) => (
          <button
            key={item.key}
            ref={(node) => {
              tabRefs.current[item.key] = node;
            }}
            type="button"
            role="tab"
            id={`warranty-tab-${item.key}`}
            aria-selected={tab === item.key}
            aria-controls="warranty-tab-panel"
            tabIndex={tab === item.key ? 0 : -1}
            className={`warranty-tab${tab === item.key ? " is-active" : ""}`}
            onClick={() => choose(item.key)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div id="warranty-tab-panel" role="tabpanel" aria-labelledby={`warranty-tab-${tab}`}>
        {tab !== "models" ? (
          <ComponentIcons key={country} country={country} accessToken={accessToken} />
        ) : (
          <ModelImages key={country} country={country} accessToken={accessToken} />
        )}
      </div>
    </WarrantyPage>
  );
};

export default CatalogueImages;
